/**
 * Hook that listens for text selection changes in the Word document
 * and automatically provides anchor data for the comment composer.
 *
 * Uses Office.js DocumentSelectionChanged event to detect when
 * the user selects text, then reads the selection + paragraph index.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { getSelectedTextForAnchor } from './useCommentControls'
import { devLog } from '../services/devLog'

export interface AnchorData {
  text: string
  paragraphIndex: number
}

/**
 * Auto-detects text selection in Word and returns anchor data.
 * Returns null when no text is selected (just a cursor position).
 *
 * @param enabled - Whether to listen for selection changes (disable in compact mode)
 */
export function useSelectionAnchor(enabled = true) {
  const [anchor, setAnchor] = useState<AnchorData | null>(null)
  const [isAutoDetected, setIsAutoDetected] = useState(false)
  const isAutoDetectedRef = useRef(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Keep ref in sync with state to avoid stale closures
  isAutoDetectedRef.current = isAutoDetected

  // Read selection with debounce to avoid excessive Word API calls
  const checkSelection = useCallback(async () => {
    if (!enabled) return

    try {
      const selected = await getSelectedTextForAnchor()
      if (selected && selected.text.length > 0) {
        setAnchor(selected)
        setIsAutoDetected(true)
        devLog.info('SelectionAnchor', `Auto-detected: "${selected.text.substring(0, 30)}..."`)
      } else {
        // Only clear auto-detected anchors (not manually set ones)
        if (isAutoDetectedRef.current) {
          setAnchor(null)
          setIsAutoDetected(false)
        }
      }
    } catch (e) {
      devLog.warn('SelectionAnchor', `Failed to read selection: ${e}`)
    }
  }, [enabled])

  // Listen for Word selection changes
  useEffect(() => {
    if (!enabled) return

    const handler = () => {
      // Debounce: wait 300ms after last selection change
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(checkSelection, 300)
    }

    try {
      Office.context.document.addHandlerAsync(
        Office.EventType.DocumentSelectionChanged,
        handler,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            devLog.info('SelectionAnchor', 'Listening for selection changes')
          } else {
            devLog.warn('SelectionAnchor', `Failed to add handler: ${result.error?.message}`)
          }
        },
      )
    } catch (e) {
      devLog.warn('SelectionAnchor', `Could not register handler: ${e}`)
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      try {
        Office.context.document.removeHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          { handler },
          () => {},
        )
      } catch {
        // Best-effort cleanup
      }
    }
  }, [enabled, checkSelection])

  /** Manually set anchor (from the anchor button) */
  const setManualAnchor = useCallback((data: AnchorData | null) => {
    setAnchor(data)
    setIsAutoDetected(false)
  }, [])

  /** Clear anchor */
  const clearAnchor = useCallback(() => {
    setAnchor(null)
    setIsAutoDetected(false)
  }, [])

  return {
    anchor,
    isAutoDetected,
    setManualAnchor,
    clearAnchor,
  }
}
